import { useQuery } from "@apollo/client";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, useTheme, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { StaffOnly } from "components/StaffOnly";
import { SubmissionForm } from "components/Submission/SubmissionForm";
import { SubmissionHeaderNavigation } from "components/Submission/SubmissionHeaderNavigation";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { runsInteractiveStepper } from "encamp-shared/src/submissions/wasteSubmissionForms";
import { GetVirtualEventForSubmissionQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCreateStepperSubmission } from "hooks/useCreateStepperSubmission";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useGetSubmission } from "hooks/useSubmissionMutations";
import { DateTime } from "luxon";
import { GET_VIRTUAL_EVENT_FOR_SUBMISSION_QUERY } from "queries/submissions";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { stepperFlagForSubmission } from "util/wasteStepperFlags";

import { getSavedWasteSubmissionsUrl } from "../navigation";

type VirtualEvent = GetVirtualEventForSubmissionQuery["virtualEvent"];

/** `deciding` is the window before the answer is known — neither redirect nor form. */
type GuidedRoute = { kind: "form" } | { kind: "deciding" } | { kind: "redirect"; to: string };

// The archetype must resolve before deciding: the submission is a network round
// trip while the flag map is already warm, so treating an unresolved archetype
// as an answer bounces every edit link on a cold cache.
function useGuidedEditRoute(args: {
  tenantId?: string;
  submissionId?: string;
  isEditMode: boolean;
}): GuidedRoute {
  const { featureFlags, loading: flagsLoading } = useFeatureFlags();
  const { data, loading } = useGetSubmission(args.submissionId ?? "", !args.isEditMode);
  if (flagsLoading || (loading && !data)) {
    return { kind: "deciding" };
  }
  const usesInteractiveStepper = data?.submission?.usesInteractiveStepper;
  if (usesInteractiveStepper === undefined) {
    return { kind: "form" };
  }
  return featureFlags[stepperFlagForSubmission(usesInteractiveStepper)] === true
    ? { kind: "redirect", to: `/o/${args.tenantId}/waste/submissions/${args.submissionId}/stepper` }
    : { kind: "form" };
}

// A `?taskId=` link materializes its occurrence straight into the stepper,
// mirroring the submissions list's row click — the fulfillment dashboard's
// virtual rows have no other create path.
function useGuidedCreateRoute(args: {
  tenantId?: string;
  submissionsUrl: string;
  isStaff: boolean;
  taskId: string | null;
  startDate: string | null;
  virtualEvent: VirtualEvent;
  virtualEventLoading: boolean;
}): GuidedRoute {
  const { featureFlags, loading: flagsLoading } = useFeatureFlags();
  const { createStepperSubmission } = useCreateStepperSubmission();
  const alerts = useAlerts();
  const [materializedId, setMaterializedId] = useState<string | null>(null);
  const [materializeFailed, setMaterializeFailed] = useState(false);
  const materializeStarted = useRef(false);

  const submissionType = args.virtualEvent?.submission?.submissionType;
  const submissionMethod = args.virtualEvent?.submission?.submissionMethod;
  // No persisted steps yet, so the archetype it would be created as picks the flag.
  const flagOn =
    !!submissionType &&
    (runsInteractiveStepper(
      submissionType,
      submissionMethod,
      featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true
    ) ||
      featureFlags[FeatureFlagKey.WasteDocumentSubmissionStepper] === true);

  const canMaterialize =
    args.isStaff && !!args.taskId && !!args.startDate && !!submissionType && flagOn;

  useEffect(() => {
    if (!canMaterialize || materializeStarted.current || !submissionType || !args.startDate) {
      return;
    }
    materializeStarted.current = true;
    createStepperSubmission({
      submissionType,
      submissionMethod,
      facilityIds: (args.virtualEvent?.facilities ?? []).map((facility) => facility.id),
      title: args.virtualEvent?.title ?? "",
      dueDate: DateTime.fromISO(args.startDate),
      virtualEventId: `${args.taskId}_${args.startDate}`,
    })
      .then((id) => {
        // A create that returns no id can't be navigated to; fall back rather
        // than leave the route deciding forever.
        if (id) setMaterializedId(id);
        else setMaterializeFailed(true);
      })
      .catch((error) => {
        setMaterializeFailed(true);
        alerts.error("Failed to create the submission.", error);
      });
  }, [
    canMaterialize,
    submissionType,
    submissionMethod,
    args.virtualEvent,
    args.taskId,
    args.startDate,
    createStepperSubmission,
    alerts,
  ]);

  if (flagsLoading || (args.taskId && args.startDate && args.virtualEventLoading)) {
    return { kind: "deciding" };
  }
  if (materializedId) {
    return {
      kind: "redirect",
      to: `/o/${args.tenantId}/waste/submissions/${materializedId}/stepper`,
    };
  }
  if (canMaterialize) {
    return materializeFailed ? { kind: "form" } : { kind: "deciding" };
  }
  const guidedCreateAvailable =
    featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true ||
    featureFlags[FeatureFlagKey.WasteDocumentSubmissionStepper] === true;
  // A bare `/create` has nothing to materialize, so the list's dialog takes over.
  // A task link the guided flow doesn't cover keeps this form — bouncing it would
  // drop the occurrence it was opened for.
  return guidedCreateAvailable && !args.taskId
    ? { kind: "redirect", to: args.submissionsUrl }
    : { kind: "form" };
}

export function CreateEditSubmission() {
  const { tenantId, submissionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isStaff, user } = useCurrentUser();

  const isEditMode = Boolean(submissionId);

  const submissionsUrl = getSavedWasteSubmissionsUrl(tenantId ?? "", user?.email);

  // Extract and log taskId and startDate parameters
  const taskId = searchParams.get("taskId");

  // Expect format YYYY-MM-DD
  const startDate = searchParams.get("startDate");

  // Query the virtual event data
  const { data: virtualEventData, loading: virtualEventLoading } = useQuery(
    GET_VIRTUAL_EVENT_FOR_SUBMISSION_QUERY,
    {
      variables: {
        taskId: taskId || "",
        deadline: startDate || "",
      },
      skip: !taskId || !startDate || isEditMode,
      fetchPolicy: "network-only",
    }
  );

  // Prepare form data from virtual event - memoized to prevent unnecessary form resets
  const formData = useMemo(() => {
    return virtualEventData?.virtualEvent
      ? {
          title: virtualEventData.virtualEvent.title ?? undefined,
          facilityIds: virtualEventData.virtualEvent.facilities?.map((f) => f.id) || [],
          dueDate: startDate,
          submissionType: virtualEventData.virtualEvent.submission?.submissionType,
          notes: virtualEventData.virtualEvent.submission?.notes ?? undefined,
          recipient: virtualEventData.virtualEvent.submission?.recipient ?? undefined,
          submissionMethod: virtualEventData.virtualEvent.submission?.submissionMethod,
        }
      : null;
  }, [virtualEventData?.virtualEvent, startDate]);

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions", to: submissionsUrl },
    { label: isEditMode ? "Edit Submission" : "Create Submission" },
  ]);

  const handleSubmissionSaved = () => {
    // Navigate back to the submissions list, preserving filters
    navigate(submissionsUrl);
  };

  const handleCancel = () => {
    navigate(submissionsUrl);
  };

  const editRoute = useGuidedEditRoute({ tenantId, submissionId, isEditMode });
  const createRoute = useGuidedCreateRoute({
    tenantId,
    submissionsUrl,
    isStaff,
    taskId,
    startDate,
    virtualEvent: virtualEventData?.virtualEvent ?? null,
    virtualEventLoading,
  });
  const guided = isEditMode ? editRoute : createRoute;
  if (guided.kind === "deciding") {
    return null;
  }
  if (guided.kind === "redirect") {
    return <Navigate to={guided.to} replace />;
  }

  return (
    <SubmissionFormPage
      tenantId={tenantId}
      submissionId={submissionId}
      isEditMode={isEditMode}
      isStaff={isStaff}
      virtualTaskId={taskId && startDate ? `${taskId}_${startDate}` : undefined}
      initialFormData={formData ?? undefined}
      onClose={handleCancel}
      onSubmit={handleSubmissionSaved}
    />
  );
}

function SubmissionFormPage({
  tenantId,
  submissionId,
  isEditMode,
  isStaff,
  virtualTaskId,
  initialFormData,
  onClose,
  onSubmit,
}: {
  tenantId?: string;
  submissionId?: string;
  isEditMode: boolean;
  isStaff: boolean;
  virtualTaskId?: string;
  initialFormData?: React.ComponentProps<typeof SubmissionForm>["initialFormData"];
  onClose: () => void;
  onSubmit: () => void;
}) {
  const theme = useTheme();
  const redirectTo = isEditMode
    ? `/o/${tenantId}/waste/submissions/${submissionId}`
    : `/o/${tenantId}/waste/submissions`;

  return (
    <StaffOnly redirectTo={redirectTo}>
      <Stack>
        <SubmissionHeaderNavigation
          tenantId={tenantId ?? ""}
          actions={
            isStaff && isEditMode ? (
              <Button
                component="a"
                href={`/staff/fulfillment/waste/submissions/${submissionId}`}
                target="_blank"
                variant="outlined"
                color="staff"
                endIcon={<OpenInNewIcon />}
              >
                Open Fulfillment
              </Button>
            ) : undefined
          }
        />

        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.grey50.light
                : theme.palette.grey50.dark,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <SubmissionForm
              onClose={onClose}
              onSubmit={onSubmit}
              submissionId={submissionId}
              isEditMode={isEditMode}
              virtualTaskId={virtualTaskId}
              initialFormData={initialFormData}
            />
          </Box>
        </Box>
      </Stack>
    </StaffOnly>
  );
}
